import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Modal, Form, Button, Header, Checkbox, Dropdown, Divider, Segment, Icon } from 'semantic-ui-react';

const CADENCE_OPTIONS = [
    { key: 'weekly', value: 'weekly', text: 'Weekly (resets Monday)' },
    { key: 'monthly', value: 'monthly', text: 'Monthly (resets on the 1st)' },
    { key: 'quarterly', value: 'quarterly', text: 'Quarterly' },
    { key: 'custom', value: 'custom', text: 'Custom (fixed start + repeating cycle)' },
];

function newGroupId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `grp-${Math.random().toString(36).slice(2)}`;
}

const EMAIL_GROUP_OPTIONS = [
    { value: 'companyAdmins', text: 'Company Admins' },
    { value: 'executives', text: 'Executives' },
    { value: 'managers', text: 'Managers (Sales + Senior)' },
    { value: 'all', text: 'Everyone' },
];

const SCORING_MODE_OPTIONS = [
    { key: 'easy', value: 'easy', text: 'Easy — every completed practice scores full points' },
    { key: 'regular', value: 'regular', text: 'Regular — only yellow/green score; red scores nothing' },
    { key: 'hard', value: 'hard', text: 'Hard — red: 0, yellow: full, green: 1.5×' },
];

// Split on commas / semicolons / whitespace, keep anything that looks like an email, de-dupe.
function parseEmails(text) {
    return Array.from(new Set(
        String(text || '').split(/[\s,;]+/).map(s => s.trim()).filter(s => s.includes('@'))
    ));
}

const LeaderboardSettings = observer(({ companyStore, usersStore, open, onClose, onSaved }) => {
    const company = companyStore?.company;
    const [enabled, setEnabled] = useState(false);
    const [cadence, setCadence] = useState('monthly');
    const [customStart, setCustomStart] = useState('');
    const [customLengthDays, setCustomLengthDays] = useState(14);
    const [groups, setGroups] = useState([]);
    const [pendingManager, setPendingManager] = useState({}); // groupId -> managerUserId
    const [scoringMode, setScoringMode] = useState('easy');
    const [emailEnabled, setEmailEnabled] = useState(false);
    const [emailGroups, setEmailGroups] = useState([]);
    const [emailCustomText, setEmailCustomText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open && company) {
            setEnabled(!!company.leaderboardEnabled);
            setCadence(company.leaderboardResetCadence || 'monthly');
            setCustomStart(company.leaderboardCustomStart || '');
            setCustomLengthDays(company.leaderboardCustomLengthDays || 14);
            setGroups((company.leaderboardGroups || []).map(g => ({
                id: g.id, name: g.name, memberIds: Array.from(g.memberIds || []),
            })));
            setPendingManager({});
            setScoringMode(company.leaderboardScoringMode || 'easy');
            setEmailEnabled(!!company.leaderboardEmailEnabled);
            setEmailGroups(Array.from(company.leaderboardEmailGroups || []));
            setEmailCustomText((company.leaderboardEmailCustom || []).join(', '));
            setError('');
        }
    }, [open, company]);

    // Options (component is an observer, so reading the stores here stays reactive).
    const companyUsers = (usersStore?.companyUsers || []).filter(u => u.userType !== 'admin');
    const memberOptions = companyUsers
        .map(u => ({ key: u.userId, value: u.userId, text: `${u.firstName} ${u.lastName} — ${u.userType}` }))
        .sort((a, b) => a.text.localeCompare(b.text));
    const managerOptions = [...(usersStore?.seniorManagers || []), ...(usersStore?.salesManagers || [])]
        .map(m => ({ key: m.userId, value: m.userId, text: `${m.firstName} ${m.lastName} — ${m.userType}` }));

    const addGroup = () => setGroups(prev => [...prev, { id: newGroupId(), name: '', memberIds: [] }]);
    const deleteGroup = (groupId) => setGroups(prev => prev.filter(g => g.id !== groupId));
    const renameGroup = (groupId, name) => setGroups(prev => prev.map(g => (g.id === groupId ? { ...g, name } : g)));

    // One group per person: when members are added to a group, strip them from any other.
    const setGroupMembers = (groupId, memberIds) => {
        const idsSet = new Set(memberIds);
        setGroups(prev => prev.map(g => (
            g.id === groupId
                ? { ...g, memberIds }
                : { ...g, memberIds: g.memberIds.filter(id => !idsSet.has(id)) }
        )));
    };

    const addManagerReports = (groupId, managerUserId) => {
        if (!managerUserId) return;
        const manager = companyUsers.find(u => u.userId === managerUserId);
        if (!manager) return;
        const downline = manager.userType === 'Senior Manager'
            ? usersStore.getSeniorManagerVisibleUsers(managerUserId).map(u => u.userId)
            : usersStore.getDirectReports(managerUserId).map(u => u.userId);
        const teamIds = [managerUserId, ...downline];
        setGroups(prev => {
            const target = prev.find(g => g.id === groupId);
            const merged = Array.from(new Set([...(target?.memberIds || []), ...teamIds]));
            const mergedSet = new Set(merged);
            return prev.map(g => (
                g.id === groupId
                    ? { ...g, memberIds: merged }
                    : { ...g, memberIds: g.memberIds.filter(id => !mergedSet.has(id)) }
            ));
        });
        setPendingManager(prev => ({ ...prev, [groupId]: '' }));
    };

    const handleSave = async () => {
        if (cadence === 'custom') {
            if (!customStart) { setError('Please choose a start date for the custom cadence.'); return; }
            if (!customLengthDays || Number(customLengthDays) < 1) {
                setError('Cycle length must be at least 1 day.');
                return;
            }
        }
        if (groups.some(g => !g.name.trim())) {
            setError('Every group needs a name (or delete the empty one).');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await companyStore.company.updateFromSnapshot({
                leaderboardEnabled: enabled,
                leaderboardResetCadence: cadence,
                // null => patchHandler REMOVEs the attribute. Clear custom fields unless selected.
                leaderboardCustomStart: cadence === 'custom' ? customStart : null,
                leaderboardCustomLengthDays: cadence === 'custom' ? Number(customLengthDays) : null,
                leaderboardScoringMode: scoringMode,
                leaderboardGroups: groups.map(g => ({ id: g.id, name: g.name.trim(), memberIds: g.memberIds })),
                leaderboardEmailEnabled: emailEnabled,
                leaderboardEmailGroups: emailGroups,
                leaderboardEmailCustom: parseEmails(emailCustomText),
            });
            // Tell the widget to refetch — scoringMode/groups changes alter points and
            // sub-tables but don't touch any of its useEffect deps.
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save leaderboard settings:', err);
            setError(err.message || 'Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} size="small" closeIcon>
            <Header icon="trophy" content="Leaderboard Settings" style={{ fontFamily: 'Montserrat' }} />
            <Modal.Content scrolling>
                <p style={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                    The leaderboard ranks team members by practice points: any elevator pitch is worth
                    1 point, every other practice type is worth 2. Only names and point totals are shown,
                    never grades or feedback. Executives and Company Admins are hidden unless they opt in.
                </p>
                <Form>
                    <Form.Field>
                        <Checkbox
                            toggle
                            label="Show the leaderboard to this company"
                            checked={enabled}
                            onChange={(_, d) => setEnabled(d.checked)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label style={{ fontFamily: 'Montserrat' }}>Reset cadence</label>
                        <Form.Select
                            options={CADENCE_OPTIONS}
                            value={cadence}
                            onChange={(_, d) => setCadence(d.value)}
                        />
                    </Form.Field>
                    {cadence === 'custom' && (
                        <>
                            <Form.Field>
                                <label style={{ fontFamily: 'Montserrat' }}>Start date</label>
                                <input
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label style={{ fontFamily: 'Montserrat' }}>Cycle length (days)</label>
                                <Form.Input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={customLengthDays}
                                    onChange={(e) => setCustomLengthDays(e.target.value)}
                                    placeholder="e.g., 14 for a two-week cycle"
                                />
                            </Form.Field>
                        </>
                    )}

                    <Form.Field>
                        <label style={{ fontFamily: 'Montserrat' }}>Scoring difficulty</label>
                        <Form.Select
                            options={SCORING_MODE_OPTIONS}
                            value={scoringMode}
                            onChange={(_, d) => setScoringMode(d.value)}
                        />
                        <p style={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#888', marginTop: '4px' }}>
                            Applies to the current period immediately. Base points (elevator 1, every other practice 2) are gated by grade.
                        </p>
                    </Form.Field>

                    <Divider />
                    <Header as="h4" style={{ fontFamily: 'Montserrat', marginBottom: '4px' }}>Sub-tables (groups)</Header>
                    <p style={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                        Optional. Create named tables (e.g. by region or team) and assign people. Each person
                        can be in one group. Sales reps see the company table plus their own group; managers,
                        execs, and admins can browse every group.
                    </p>

                    {groups.map(group => (
                        <Segment key={group.id} style={{ fontFamily: 'Montserrat' }}>
                            <Form.Group widths="equal" style={{ alignItems: 'flex-end', marginBottom: '8px' }}>
                                <Form.Input
                                    label="Group name"
                                    placeholder="e.g. North Region"
                                    value={group.name}
                                    onChange={(e) => renameGroup(group.id, e.target.value)}
                                />
                                <div style={{ flex: '0 0 auto' }}>
                                    <Button
                                        type="button"
                                        icon
                                        labelPosition="left"
                                        color="red"
                                        basic
                                        onClick={() => deleteGroup(group.id)}
                                    >
                                        <Icon name="trash" /> Delete
                                    </Button>
                                </div>
                            </Form.Group>
                            <Form.Field>
                                <label>Members ({group.memberIds.length})</label>
                                <Dropdown
                                    placeholder="Add members…"
                                    fluid multiple search selection
                                    options={memberOptions}
                                    value={group.memberIds}
                                    onChange={(_, { value }) => setGroupMembers(group.id, value)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Quick add: a manager &amp; their reports</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Dropdown
                                        placeholder="Choose a manager…"
                                        search selection clearable
                                        style={{ flex: 1 }}
                                        options={managerOptions}
                                        value={pendingManager[group.id] || ''}
                                        onChange={(_, { value }) => setPendingManager(prev => ({ ...prev, [group.id]: value }))}
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => addManagerReports(group.id, pendingManager[group.id])}
                                        disabled={!pendingManager[group.id]}
                                    >
                                        Add
                                    </Button>
                                </div>
                            </Form.Field>
                        </Segment>
                    ))}

                    <Button type="button" basic icon labelPosition="left" onClick={addGroup} style={{ fontFamily: 'Montserrat' }}>
                        <Icon name="plus" /> Add group
                    </Button>

                    <Divider />
                    <Header as="h4" style={{ fontFamily: 'Montserrat', marginBottom: '4px' }}>Email final standings</Header>
                    <p style={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                        When a period resets, email the final standings (names + points only) to the people you choose.
                        Sends around mid-morning the day the period rolls over.
                    </p>
                    <Form.Field>
                        <Checkbox
                            toggle
                            label="Email the final standings at each reset"
                            checked={emailEnabled}
                            onChange={(_, d) => setEmailEnabled(d.checked)}
                        />
                    </Form.Field>
                    {emailEnabled && (
                        <Segment style={{ fontFamily: 'Montserrat' }}>
                            <Form.Field>
                                <label>Send to</label>
                                {EMAIL_GROUP_OPTIONS.map(opt => (
                                    <Checkbox
                                        key={opt.value}
                                        style={{ display: 'block', marginBottom: '6px' }}
                                        label={opt.text}
                                        checked={emailGroups.includes(opt.value)}
                                        onChange={(_, d) => setEmailGroups(prev =>
                                            d.checked
                                                ? Array.from(new Set([...prev, opt.value]))
                                                : prev.filter(v => v !== opt.value)
                                        )}
                                    />
                                ))}
                            </Form.Field>
                            <Form.Field>
                                <label>Plus custom email addresses</label>
                                <textarea
                                    rows={2}
                                    placeholder="cfo@acme.com, board@acme.com"
                                    value={emailCustomText}
                                    onChange={(e) => setEmailCustomText(e.target.value)}
                                />
                            </Form.Field>
                        </Segment>
                    )}

                    {error && (
                        <p style={{ color: '#dc3545', fontFamily: 'Montserrat', fontSize: '13px', marginTop: '12px' }}>{error}</p>
                    )}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} style={{ fontFamily: 'Montserrat' }}>Cancel</Button>
                <Button
                    primary
                    loading={saving}
                    onClick={handleSave}
                    style={{ fontFamily: 'Montserrat', backgroundColor: '#137fa2' }}
                >
                    Save
                </Button>
            </Modal.Actions>
        </Modal>
    );
});

export default LeaderboardSettings;
